/**
 * Vector Search Service
 * 
 * Handles vector similarity search for RAG (Retrieval Augmented Generation).
 * Uses Firestore Vector Search extension for scalable ANN search.
 * Includes reranking, MMR, hybrid search, and context window management.
 */

import { documentProcessingService } from './documentProcessingService';
import { documentAuditService } from './documentAuditService';
import { firebaseApp, db } from '@healthdesk/shared-services';
import { extractMedicalEntities } from './medicalEntityExtractionService';

class VectorSearchService {
  /**
   * Search for relevant document chunks for a query with advanced retrieval
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {string} queryText - Query text to search for
   * @param {number} topK - Number of chunks to return (default: 10)
   * @param {Object} options - { adminId, minConfidence, useReranking, useMMR, useHybridSearch, embeddingModel }
   * @returns {Promise<Array>} Array of relevant chunks with text and metadata
   */
  async searchRelevantChunks(baseQueryId, position, queryText, topK = 10, options = {}) {
    const { 
      minConfidence = 0.7,
      useReranking = true,
      useMMR = true,
      useHybridSearch = false,
      embeddingModel = 'text-embedding-3-large',
    } = options;
    
    // Step 1: Query preprocessing - extract medical entities
    const medicalEntities = extractMedicalEntities(queryText);
    
    // Step 2: Generate embedding for query text
    const generateEmbeddingFunction = firebaseApp.functions().httpsCallable('generateEmbedding');
    const result = await generateEmbeddingFunction({ text: queryText, model: embeddingModel });
    const queryEmbedding = result.data.embedding;
    
    // Step 3: Semantic search (vector search)
    // Get more candidates for reranking/MMR
    const candidateCount = useReranking || useMMR ? topK * 3 : topK * 2;
    let chunks = await documentProcessingService.searchSimilarChunks(
      baseQueryId,
      position,
      queryEmbedding,
      candidateCount,
      { ...options, specialty: medicalEntities.anatomy[0] } // Use anatomy for specialty filter if available
    );
    
    // Step 4: Hybrid search - combine with keyword search if enabled
    if (useHybridSearch && medicalEntities.allTerms.length > 0) {
      const keywordChunks = await this.keywordSearch(baseQueryId, position, medicalEntities.allTerms, topK, options);
      // Merge and deduplicate
      const chunkMap = new Map();
      chunks.forEach(chunk => chunkMap.set(chunk.chunkId || chunk.index, chunk));
      keywordChunks.forEach(chunk => {
        const key = chunk.chunkId || chunk.index;
        if (chunkMap.has(key)) {
          // Boost score for chunks found in both searches
          chunkMap.get(key).similarity = (chunkMap.get(key).similarity || 0) * 1.2;
        } else {
          chunkMap.set(key, { ...chunk, similarity: chunk.similarity || 0.5 });
        }
      });
      chunks = Array.from(chunkMap.values());
    }
    
    // Step 5: Filter by confidence threshold
    let filteredChunks = chunks.filter(chunk => (chunk.similarity || 0) >= minConfidence);
    
    // Step 6: Reranking with cross-encoder (if enabled)
    if (useReranking && filteredChunks.length > 0) {
      filteredChunks = await this.rerankChunks(queryText, filteredChunks, topK * 2);
    }
    
    // Step 7: MMR for diversity (if enabled)
    if (useMMR && filteredChunks.length > topK) {
      filteredChunks = this.applyMMR(filteredChunks, queryEmbedding, topK);
    }
    
    // Step 8: Final filtering and sorting
    filteredChunks = filteredChunks
      .sort((a, b) => (b.similarity || 0) - (a.similarity || 0))
      .slice(0, topK);
    
    // Log access for retrieved documents
    const uniqueDocumentIds = [...new Set(filteredChunks.map(c => c.documentId))];
    uniqueDocumentIds.forEach(docId => {
      documentAuditService.logRetrieval(docId, options.userId || 'unknown', baseQueryId, 
        filteredChunks.filter(c => c.documentId === docId).length);
    });
    
    return filteredChunks;
  }
  
  /**
   * Keyword search using BM25-like scoring
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {Array<string>} keywords - Keywords to search for
   * @param {number} topK - Number of results
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Relevant chunks
   */
  async keywordSearch(baseQueryId, position, keywords, topK = 10, options = {}) {
    // Simple keyword matching - can be enhanced with proper BM25
    const documents = await documentProcessingService.getDocumentsForPosition(baseQueryId, position, options);
    const allChunks = [];
    
    for (const doc of documents) {
      try {
        const chunksRef = db.collection('llmDocumentVectors').doc(doc.id).collection('chunks');
        let query = chunksRef
          .where('baseQueryId', '==', baseQueryId)
          .where('position', '==', position);
        
        if (options.adminId !== undefined) {
          query = query.where('adminId', '==', options.adminId);
        }
        
        const chunksSnapshot = await query.get();
        
        chunksSnapshot.docs.forEach(chunkDoc => {
          const chunkData = chunkDoc.data();
          const text = (chunkData.text || '').toLowerCase();
          
          // Simple keyword matching score
          let score = 0;
          keywords.forEach(keyword => {
            const keywordLower = keyword.toLowerCase();
            const matches = (text.match(new RegExp(keywordLower, 'g')) || []).length;
            score += matches; // Simple frequency-based scoring
          });
          
          if (score > 0) {
            allChunks.push({
              documentId: doc.id,
              documentName: doc.fileName,
              chunkId: chunkDoc.id,
              ...chunkData,
              similarity: Math.min(score / keywords.length, 1.0), // Normalize to 0-1
            });
          }
        });
      } catch (error) {
        console.warn(`[VectorSearch] Keyword search error for document ${doc.id}:`, error);
      }
    }
    
    return allChunks
      .sort((a, b) => (b.similarity || 0) - (a.similarity || 0))
      .slice(0, topK);
  }
  
  /**
   * Rerank chunks using cross-encoder (via Cloud Function)
   * @param {string} queryText - Query text
   * @param {Array} chunks - Chunks to rerank
   * @param {number} topK - Top K to return
   * @returns {Promise<Array>} Reranked chunks
   */
  async rerankChunks(queryText, chunks, topK = 10) {
    if (chunks.length === 0) return chunks;
    
    try {
      // Call Cloud Function for reranking with cross-encoder
      // Note: This requires a Cloud Function with cross-encoder model
      const rerankFunction = firebaseApp.functions().httpsCallable('rerankChunks');
      const result = await rerankFunction({
        query: queryText,
        chunks: chunks.map(c => ({
          id: c.chunkId || c.index,
          text: c.text,
        })),
        topK,
      });
      
      // Map reranked scores back to chunks
      const rerankedMap = new Map(
        result.data.scores.map((item, idx) => [item.id, item.score])
      );
      
      return chunks
        .map(chunk => ({
          ...chunk,
          similarity: rerankedMap.get(chunk.chunkId || chunk.index) || chunk.similarity || 0,
        }))
        .sort((a, b) => (b.similarity || 0) - (a.similarity || 0))
        .slice(0, topK);
    } catch (error) {
      console.warn('[VectorSearch] Reranking failed, using original scores:', error);
      return chunks.slice(0, topK);
    }
  }
  
  /**
   * Apply Maximal Marginal Relevance (MMR) for diversity
   * @param {Array} chunks - Chunks to diversify
   * @param {Array<number>} queryEmbedding - Query embedding
   * @param {number} topK - Number of chunks to return
   * @param {number} lambda - Diversity parameter (0 = relevance only, 1 = diversity only)
   * @returns {Array} Diversified chunks
   */
  applyMMR(chunks, queryEmbedding, topK = 10, lambda = 0.5) {
    if (chunks.length <= topK) return chunks;
    
    const selected = [];
    const remaining = [...chunks];
    
    // Start with highest relevance chunk
    selected.push(remaining.shift());
    
    while (selected.length < topK && remaining.length > 0) {
      let bestScore = -Infinity;
      let bestIdx = 0;
      
      // Find chunk with best MMR score
      for (let i = 0; i < remaining.length; i++) {
        const chunk = remaining[i];
        const relevance = chunk.similarity || 0;
        
        // Calculate max similarity to already selected chunks
        let maxSimilarity = 0;
        for (const selectedChunk of selected) {
          if (selectedChunk.embedding && chunk.embedding) {
            const sim = this.cosineSimilarity(selectedChunk.embedding, chunk.embedding);
            maxSimilarity = Math.max(maxSimilarity, sim);
          }
        }
        
        // MMR score: balance relevance and diversity
        const mmrScore = lambda * relevance - (1 - lambda) * maxSimilarity;
        
        if (mmrScore > bestScore) {
          bestScore = mmrScore;
          bestIdx = i;
        }
      }
      
      selected.push(remaining.splice(bestIdx, 1)[0]);
    }
    
    return selected;
  }
  
  /**
   * Calculate cosine similarity (helper for MMR)
   * @param {Array<number>} vecA - Vector A
   * @param {Array<number>} vecB - Vector B
   * @returns {number} Similarity score
   */
  cosineSimilarity(vecA, vecB) {
    if (!vecA || !vecB || vecA.length !== vecB.length) return 0;
    
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;
    
    for (let i = 0; i < vecA.length; i++) {
      dotProduct += vecA[i] * vecB[i];
      normA += vecA[i] * vecA[i];
      normB += vecB[i] * vecB[i];
    }
    
    const denominator = Math.sqrt(normA) * Math.sqrt(normB);
    return denominator > 0 ? dotProduct / denominator : 0;
  }

  /**
   * Estimate token count for text (rough approximation: 1 token ≈ 4 characters)
   * @param {string} text - Text to count
   * @returns {number} Estimated token count
   */
  estimateTokenCount(text) {
    if (!text) return 0;
    return Math.ceil(text.length / 4);
  }

  /**
   * Format retrieved chunks for injection into prompt with context window management
   * @param {Array} chunks - Array of chunk objects with text and metadata
   * @param {string} format - 'json' or 'text' (default: 'text')
   * @param {Object} documentMetadata - Map of documentId to full document metadata
   * @param {Object} documentSettings - { limitToVectorStore, supplementAuthoritativeResources }
   * @param {Object} contextOptions - { maxTokens, reservedTokens } for context window management
   * @returns {string} Formatted chunks ready for prompt injection
   */
  formatChunksForPrompt(chunks, format = 'text', documentMetadata = {}, documentSettings = {}, contextOptions = {}) {
    if (chunks.length === 0) {
      return '';
    }

    const { limitToVectorStore = false, supplementAuthoritativeResources = false } = documentSettings;
    const { maxTokens = 4000, reservedTokens = 500 } = contextOptions; // Default: reserve 500 tokens for prompt
    
    // Context window management: prioritize chunks and truncate if needed
    let selectedChunks = [...chunks];
    let availableTokens = maxTokens - reservedTokens;
    
    // Sort by similarity (highest first) and select chunks that fit
    selectedChunks = selectedChunks.sort((a, b) => (b.similarity || 0) - (a.similarity || 0));
    
    const finalChunks = [];
    let totalTokens = 0;
    
    for (const chunk of selectedChunks) {
      const chunkText = chunk.text || '';
      const chunkTokens = this.estimateTokenCount(chunkText);
      const citationTokens = 50; // Rough estimate for citation text
      const totalChunkTokens = chunkTokens + citationTokens;
      
      if (totalTokens + totalChunkTokens <= availableTokens) {
        finalChunks.push(chunk);
        totalTokens += totalChunkTokens;
      } else {
        // Try to fit a truncated version if there's space
        const remainingTokens = availableTokens - totalTokens - citationTokens;
        if (remainingTokens > 100) { // Only if meaningful space remains
          const truncatedText = chunkText.substring(0, remainingTokens * 4);
          finalChunks.push({
            ...chunk,
            text: truncatedText + '... [truncated]',
          });
        }
        break; // No more space
      }
    }

    if (format === 'json') {
      return JSON.stringify(chunks.map(chunk => {
        const docMeta = documentMetadata[chunk.documentId] || {};
        return {
          text: chunk.text,
          document: chunk.documentName,
          source: docMeta.metadata?.source || 'Unknown',
          author: docMeta.metadata?.author || 'Unknown',
          publicationDate: docMeta.metadata?.publicationDate || null,
          similarity: chunk.similarity,
        };
      }), null, 2);
    }

    // Text format - more readable with citations
    const formatted = finalChunks.map((chunk, index) => {
      const docMeta = documentMetadata[chunk.documentId] || {};
      const source = docMeta.metadata?.source || chunk.documentName;
      const author = docMeta.metadata?.author;
      const pubDate = docMeta.metadata?.publicationDate;
      
      let citation = `[Source: ${source}`;
      if (author) citation += `, ${author}`;
      if (pubDate) citation += `, ${pubDate}`;
      citation += `, Relevance: ${(chunk.similarity * 100).toFixed(1)}%]`;
      
      return `${citation}\n${chunk.text}`;
    }).join('\n\n---\n\n');

    // Build instruction text based on settings (mutually exclusive)
    let instructionText = 'IMPORTANT: When referencing information from the above documents, always cite the source in your response.';
    
    if (limitToVectorStore) {
      instructionText += '\n\nCRITICAL CONSTRAINT: You MUST ONLY use information from the documents provided above. Do NOT reference other medical sources, guidelines, or general medical knowledge. If information is not found in the provided documents, state that clearly rather than using information from other sources.';
    } else if (supplementAuthoritativeResources) {
      instructionText += '\n\nSUPPLEMENTATION MODE: Use the documents provided above to supplement your research with authoritative medical resources (peer-reviewed guidelines, UpToDate, medical textbooks, etc.) in the relevant medical specialty. Combine information from both the provided documents and established authoritative sources, giving appropriate weight to each. The provided documents should enhance and complement, not replace, authoritative medical knowledge.';
    } else {
      // Default: Use documents but also allow other sources (no restriction)
      instructionText += '\n\nYou may use the documents provided above along with your general medical knowledge and authoritative sources as appropriate.';
    }

    return `\n\n=== RELEVANT DOCUMENT CONTEXT (with citations) ===\n${formatted}\n=== END DOCUMENT CONTEXT ===\n\n${instructionText}`;
  }

  /**
   * Inject relevant chunks into a prompt template
   * @param {string} promptTemplate - Original prompt template
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {string} queryText - Query text (for similarity search)
   * @param {Object} options - { adminId, topK, format, minConfidence, userId, documentSettings }
   * @returns {Promise<string>} Prompt with injected document context
   */
  async injectDocumentContext(promptTemplate, baseQueryId, position, queryText, options = {}) {
    const { 
      adminId, 
      topK = 10, 
      format = 'text', 
      minConfidence = 0.7, 
      userId, 
      documentSettings = {},
      useReranking = true,
      useMMR = true,
      useHybridSearch = false,
      embeddingModel = 'text-embedding-3-large',
      maxTokens = 4000,
      reservedTokens = 500,
    } = options;
    
    // Search for relevant chunks with advanced retrieval
    const chunks = await this.searchRelevantChunks(
      baseQueryId,
      position,
      queryText,
      topK,
      { 
        adminId, 
        minConfidence, 
        userId,
        useReranking,
        useMMR,
        useHybridSearch,
        embeddingModel,
      }
    );
    
    if (chunks.length === 0) {
      return promptTemplate; // No documents available or all below confidence threshold
    }
    
    // Get full document metadata for citations
    const documentIds = [...new Set(chunks.map(c => c.documentId))];
    const documentMetadata = {};
    for (const docId of documentIds) {
      try {
        const doc = await documentProcessingService.getDocumentById?.(docId);
        if (doc) documentMetadata[docId] = doc;
      } catch (error) {
        console.warn(`[VectorSearch] Failed to get metadata for document ${docId}:`, error);
      }
    }
    
    // Format chunks with citations, settings-based instructions, and context window management
    const formattedChunks = this.formatChunksForPrompt(
      chunks, 
      format, 
      documentMetadata, 
      documentSettings,
      { maxTokens, reservedTokens }
    );
    
    // Inject into prompt (before the main instruction)
    // Look for common prompt patterns to insert context
    const contextMarker = '=== RELEVANT DOCUMENT CONTEXT ===';
    
    if (promptTemplate.includes(contextMarker)) {
      // Replace existing context
      const beforeContext = promptTemplate.split(contextMarker)[0];
      const afterContext = promptTemplate.split('=== END DOCUMENT CONTEXT ===')[1] || '';
      return beforeContext + formattedChunks + afterContext;
    } else {
      // Insert at beginning (after system message if present)
      const systemMessageEnd = promptTemplate.indexOf('\n\n');
      if (systemMessageEnd > 0) {
        return promptTemplate.substring(0, systemMessageEnd + 2) + 
               formattedChunks + 
               promptTemplate.substring(systemMessageEnd + 2);
      }
      return formattedChunks + promptTemplate;
    }
  }
}

// Export singleton instance
export const vectorSearchService = new VectorSearchService();
export default vectorSearchService;
